import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import { useNavigate } from "react-router-dom";

import useSmqPermissions from "../../../hooks/useSmqPermissions";
import SMQMainContainer from "../../../components/SMQMainContainer";
import ProcessusService from "../../../services/smq/ProcessusService";
import ButtonYellow from "../component/BouttonYellow";
import DirectionAdd from "./DirectionAdd";
import HeaderProcessus from "./HeaderProcessus";
import ProcessusAdd from "./ProcessusAdd";

const EMPTY_PROCESS_DIALOG = {
  initialValues: null,
  mode: "create",
  open: false,
};

const CATEGORY_COLORS = {
  controle: { backgroundColor: "#eee5ff", color: "#6732c7" },
  direction: { backgroundColor: "#e2ecf7", color: "#244b70" },
  realisation: { backgroundColor: "#e1f2e8", color: "#28734b" },
  support: { backgroundColor: "#faeadc", color: "#bd5700" },
};

const normalizeCategory = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const getProcessCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? count : 0;
};

const DirectionCard = memo(
  ({ canManage, direction, isActive, onDelete, onEdit, onSelect }) => {
  const processCount = getProcessCount(direction.processus);
  const responsableName = String(direction.responsable_name || "").trim();
  const directionCode = direction.direction_code || "—";
  const directionName = direction.direction_name || "Direction sans nom";

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: "#fff",
        border: isActive ? "2px solid #d5ad3f" : "1px solid #cbd8e6",
        borderRadius: "7px",
        boxShadow: "0 2px 7px rgba(16, 38, 61, 0.08)",
        boxSizing: "border-box",
        height: "100%",
        minHeight: 94,
        overflow: "hidden",
        position: "relative",
        transition: "border-color 160ms ease, box-shadow 160ms ease",
      }}
    >
      <ButtonBase
        aria-label={"Sélectionner la direction " + directionName}
        aria-pressed={isActive}
        focusRipple
        onClick={() => onSelect(direction.direction_id)}
        sx={{
          alignItems: "flex-start",
          display: "flex",
          height: "100%",
          justifyContent: "flex-start",
          p: 1.5,
          pr: 11,
          textAlign: "left",
          width: "100%",
          "&.Mui-focusVisible": {
            boxShadow: "inset 0 0 0 3px rgba(13, 39, 64, 0.22)",
          },
        }}
      >
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Typography
            component="h2"
            sx={{
              color: "#173d61",
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1.1,
              mb: 0.35,
            }}
          >
            {directionCode}
          </Typography>

          <Typography
            title={directionName}
            sx={{
              color: "#60758a",
              fontSize: 10,
              lineHeight: 1.3,
              mb: 0.8,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {directionName}
          </Typography>

          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexWrap: "wrap",
              gap: 0.7,
            }}
          >
            <Box
              component="span"
              sx={{
                backgroundColor: "#e7eff7",
                borderRadius: "999px",
                color: "#173d61",
                fontSize: 10,
                fontWeight: 700,
                lineHeight: 1,
                px: 0.9,
                py: 0.5,
              }}
            >
              {processCount} processus
            </Box>
            {
              responsableName &&

            <Box
              component="span"
              sx={{
                backgroundColor: responsableName ? "#e4f2e9" : "#edf1f5",
                borderRadius: "999px",
                color: responsableName ? "#28724a" : "#60758a",
                fontSize: 9,
                fontWeight: 700,
                lineHeight: 1,
                maxWidth: "100%",
                overflow: "hidden",
                px: 0.9,
                py: 0.5,
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={responsableName || ""}
            >
              {responsableName || ""}
            </Box>
    }
          </Box>
        </Box>
      </ButtonBase>

      {canManage && <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: 0.6,
          position: "absolute",
          right: 0.9,
          top: 0.8,
        }}
      >
        <Tooltip title="Modifier">
          <IconButton
            aria-label={"Modifier la direction " + directionCode}
            onClick={() => onEdit(direction)}
            size="small"
            sx={{
              borderRadius: "6px",
              color: "#10263d",
              height: 34,
              width: 34,
              "&:focus-visible": {
                outline: "3px solid rgba(13, 39, 64, 0.22)",
              },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Supprimer">
          <IconButton
            aria-label={"Supprimer la direction " + directionCode}
            onClick={() => onDelete(direction)}
            size="small"
            sx={{
              backgroundColor: "#a91e23",
              borderRadius: "7px",
              color: "#fff",
              height: 36,
              width: 44,
              "&:hover": { backgroundColor: "#8d171b" },
              "&:focus-visible": {
                outline: "3px solid rgba(169, 30, 35, 0.25)",
                outlineOffset: "2px",
              },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>}
    </Paper>
  );
  }
);

DirectionCard.displayName = "DirectionCard";

const LoadingCards = () => (
  <Grid aria-label="Chargement des directions" container spacing={1.25}>
    {Array.from({ length: 8 }, (_, index) => (
      <Grid item key={index} lg={3} sm={6} xs={12}>
        <Skeleton
          height={94}
          sx={{ borderRadius: "7px" }}
          variant="rectangular"
        />
      </Grid>
    ))}
  </Grid>
);

const Direction = () => {
  const { isAdmin: isSmqAdmin } = useSmqPermissions();
  const navigate = useNavigate();
  const [activeDirectionId, setActiveDirectionId] = useState(null);
  const [directions, setDirections] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [directionProcesses, setDirectionProcesses] = useState([]);
  const [processesLoading, setProcessesLoading] = useState(false);
  const [processesError, setProcessesError] = useState(null);
  const [processDialog, setProcessDialog] = useState(EMPTY_PROCESS_DIALOG);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [dialog, setDialog] = useState({
    initialValues: null,
    mode: "create",
    open: false,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [notification, setNotification] = useState({
    message: "",
    open: false,
    severity: "success",
  });
  const requestIdRef = useRef(0);
  const processesRequestIdRef = useRef(0);

  const activeDirection = useMemo(
    () =>
      directions.find(
        (direction) =>
          String(direction.direction_id) === String(activeDirectionId)
      ) || null,
    [activeDirectionId, directions]
  );

  const loadDirections = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setError(null);
    setLoading(true);

    try {
      const payload = await ProcessusService.getDirections();

      if (!Array.isArray(payload?.data)) {
        throw new Error(
          "Le service des directions a retourné une réponse invalide"
        );
      }

      if (requestId === requestIdRef.current) {
        setDirections(payload.data);
        setActiveDirectionId((currentId) => {
          const currentStillExists = payload.data.some(
            (direction) =>
              String(direction.direction_id) === String(currentId)
          );

          return currentStillExists ? currentId : null;
        });
      }
    } catch (requestError) {
      if (requestId === requestIdRef.current) {
        setError(
          requestError?.message || "Impossible de charger les directions"
        );
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadDirections();

    return () => {
      requestIdRef.current += 1;
      processesRequestIdRef.current += 1;
    };
  }, [loadDirections]);

  const loadDirectionProcesses = useCallback(async (directionId) => {
    if (!directionId) {
      processesRequestIdRef.current += 1;
      setDirectionProcesses([]);
      setProcessesError(null);
      setProcessesLoading(false);
      return;
    }

    const requestId = processesRequestIdRef.current + 1;
    processesRequestIdRef.current = requestId;
    setProcessesLoading(true);
    setProcessesError(null);

    try {
      const payload = await ProcessusService.getProcessusByDirection(directionId);
      if (!Array.isArray(payload?.data)) {
        throw new Error("Le service a retourné une liste de processus invalide");
      }

      if (requestId === processesRequestIdRef.current) {
        setDirectionProcesses(payload.data);
        setPaginationModel((current) => ({ ...current, page: 0 }));
      }
    } catch (requestError) {
      if (requestId === processesRequestIdRef.current) {
        setDirectionProcesses([]);
        setProcessesError(
          requestError?.message || "Impossible de charger les processus"
        );
      }
    } finally {
      if (requestId === processesRequestIdRef.current) {
        setProcessesLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadDirectionProcesses(activeDirectionId);
  }, [activeDirectionId, loadDirectionProcesses]);

  const openCreateDialog = () => {
    if (!isSmqAdmin) return;
    setDialog({ initialValues: null, mode: "create", open: true });
  };

  const openEditDialog = (direction) => {
    if (!isSmqAdmin) return;
    setActiveDirectionId(direction.direction_id);
    setDialog({
      initialValues: {
        nc_direction_id: direction.nc_direction_id,
        direction_id: direction.direction_id,
        direction_code: direction.direction_code,
        direction_name: direction.direction_name,
        note: direction.note || "",
        responsable_id: direction.responsable_id,
        service_id: direction.service_id,
        responsable_name: direction.responsable_name,
      },
      mode: "edit",
      open: true,
    });
  };

  const closeDialog = () => {
    setDialog((current) => ({ ...current, open: false }));
  };

  const handleSaved = (result = {}) => {
    const savedMode = result.mode || dialog.mode;
    if (result.direction_id !== null && result.direction_id !== undefined) {
      setActiveDirectionId(result.direction_id);
    }
    setNotification({
      message:
        savedMode === "edit"
          ? "Direction mise à jour avec succès."
          : "Direction configurée avec succès.",
      open: true,
      severity: "success",
    });
    loadDirections();
  };

  const openDeleteDialog = (direction) => {
    if (!isSmqAdmin) return;
    setActiveDirectionId(direction.direction_id);
    setDeleteError(null);
    setDeleteTarget(direction);
  };

  const closeDeleteDialog = () => {
    if (!deleting) {
      setDeleteError(null);
      setDeleteTarget(null);
    }
  };

  const handleDelete = async () => {
    if (!isSmqAdmin || !deleteTarget || deleting) {
      return;
    }

    setDeleting(true);
    setDeleteError(null);

    try {
      await ProcessusService.deleteDirection(deleteTarget.direction_id);
      const deletedDirectionId = deleteTarget.direction_id;
      setDeleteTarget(null);
      if (String(activeDirectionId) === String(deletedDirectionId)) {
        setActiveDirectionId(null);
      }
      setNotification({
        message: "Direction supprimée avec succès.",
        open: true,
        severity: "success",
      });
      await loadDirections();
    } catch (deleteRequestError) {
      const message =
        deleteRequestError?.message || "Impossible de supprimer la direction";
      setDeleteError(message);
      setNotification({ message, open: true, severity: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const openCreateProcessDialog = () => {
    if (!isSmqAdmin || !activeDirection) return;
    setProcessDialog({
      initialValues: { direction_id: activeDirection.direction_id },
      mode: "create",
      open: true,
    });
  };

  const openEditProcessDialog = useCallback((processus) => {
    if (!isSmqAdmin) return;
    setProcessDialog({
      initialValues: processus,
      mode: "edit",
      open: true,
    });
  }, [isSmqAdmin]);

  const closeProcessDialog = () => {
    setProcessDialog((current) => ({ ...current, open: false }));
  };

  const handleProcessSaved = async () => {
    const wasEdit = processDialog.mode === "edit";
    setNotification({
      message: wasEdit
        ? "Processus mis à jour avec succès."
        : "Processus créé avec succès.",
      open: true,
      severity: "success",
    });
    await Promise.all([
      loadDirections(),
      loadDirectionProcesses(activeDirectionId),
    ]);
  };

  const processColumns = useMemo(
    () => [
      {
        field: "referentiel_nom",
        flex: 1.4,
        headerName: "Processus",
        minWidth: 260,
        renderCell: ({ row }) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography color="#10263d" fontSize={12} fontWeight={700} noWrap>
              {row.referentiel_nom || "—"}
            </Typography>
            <Typography color="#60758a" fontFamily="monospace" fontSize={10} noWrap>
              {row.instance || "—"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "categorie_name",
        flex: 0.8,
        headerName: "Catégorie",
        minWidth: 155,
        renderCell: ({ value }) => {
          const colors = CATEGORY_COLORS[normalizeCategory(value)] || {
            backgroundColor: "#edf2f7",
            color: "#52677c",
          };
          return (
            <Box
              component="span"
              sx={{
                ...colors,
                borderRadius: "999px",
                fontSize: 10,
                fontWeight: 700,
                px: 1,
                py: 0.45,
              }}
            >
              {value || "—"}
            </Box>
          );
        },
      },
      {
        field: "pilote_name",
        flex: 1,
        headerName: "Pilote",
        minWidth: 210,
        valueGetter: (value) => String(value || "").trim() || "—",
      },
      {
        align: "center",
        field: "actions",
        filterable: false,
        headerAlign: "center",
        headerName: "Actions",
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <Box sx={{ alignItems: "center", display: "flex", gap: 0.7 }}>
            <Tooltip title="Ouvrir les outils du processus">
              <Button
                aria-label={`Ouvrir les outils de ${row.instance}`}
                onClick={() =>
                  navigate(`/smq_nc/processus/outils/${row.processus_id}`)
                }
                startIcon={<BuildOutlinedIcon fontSize="small" />}
                size="small"
                sx={{
                  backgroundColor: "#173d61",
                  borderRadius: "7px",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  height: 34,
                  minWidth: 92,
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#102f4c" },
                }}
                variant="contained"
              >
                Outils
              </Button>
            </Tooltip>
            {isSmqAdmin && <Tooltip title="Modifier le processus">
              <IconButton
                aria-label={`Modifier ${row.instance}`}
                onClick={() => openEditProcessDialog(row)}
                size="small"
                sx={{
                  backgroundColor: "rgba(23, 61, 97, 0.08)",
                  borderRadius: "7px",
                  color: "#173d61",
                  height: 34,
                  width: 34,
                  "&:hover": { backgroundColor: "rgba(23, 61, 97, 0.16)" },
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>}
          </Box>
        ),
      },
    ],
    [isSmqAdmin, navigate, openEditProcessDialog]
  );

  return (
    <SMQMainContainer>
      <>
        <HeaderProcessus
          icon={
            <BusinessOutlinedIcon
              aria-hidden="true"
              sx={{ color: "#d4a733", fontSize: 16 }}
            />
          }
          title="Direction"
        />

        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 3 }}>
          <Grid
            alignItems="center"
            container
            justifyContent="space-between"
            spacing={2}
          >
            <Grid item sm={6} xs={12}>
              <Typography
                color="#10263d"
                fontWeight={700}
                gutterBottom
                variant="h6"
              >
                Configuration des Directions
              </Typography>
            </Grid>
            <Grid
              item
              sm={6}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
              xs={12}
            >
              {isSmqAdmin && <ButtonYellow onClick={openCreateDialog}>
                + Configurer Direction
              </ButtonYellow>}
            </Grid>
          </Grid>

          {error && (
            <Alert
              action={
                <Button color="inherit" onClick={loadDirections} size="small">
                  Réessayer
                </Button>
              }
              severity="error"
              sx={{ mt: 2 }}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            {loading ? (
              <LoadingCards />
            ) : directions.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  border: "1px dashed #9fb1c3",
                  borderRadius: "7px",
                  color: "#60758a",
                  p: 4,
                  textAlign: "center",
                }}
              >
                <Typography fontSize={13} fontWeight={600}>
                  Aucune direction disponible.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={1.25}>
                {directions.map((direction) => (
                  <Grid item key={direction.direction_id} lg={3} sm={6} xs={12}>
                    <DirectionCard
                      canManage={isSmqAdmin}
                      direction={direction}
                      isActive={
                        String(activeDirectionId) ===
                        String(direction.direction_id)
                      }
                      onDelete={openDeleteDialog}
                      onEdit={openEditDialog}
                      onSelect={setActiveDirectionId}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          {activeDirection && (
            <Paper
              elevation={0}
              sx={{
                border: "1px solid #cbd8e6",
                borderRadius: "10px",
                mt: 2.5,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  alignItems: { xs: "flex-start", sm: "center" },
                  backgroundColor: "#edf3f8",
                  borderBottom: "1px solid #cbd8e6",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.4,
                }}
              >
                <Typography color="#173d61" fontSize={15} fontWeight={800}>
                  <BusinessOutlinedIcon
                    aria-hidden="true"
                    sx={{ fontSize: 17, mr: 0.7, verticalAlign: "text-bottom" }}
                  />
                  {activeDirection.direction_code} — {activeDirection.direction_name}
                </Typography>
                {isSmqAdmin && <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}>
                  <Button
                    onClick={() => openEditDialog(activeDirection)}
                    startIcon={<EditOutlinedIcon />}
                    sx={{ color: "#10263d", textTransform: "none" }}
                  >
                    Modifier
                  </Button>
                  <Button
                    color="error"
                    onClick={() => openDeleteDialog(activeDirection)}
                    startIcon={<DeleteOutlineIcon />}
                    sx={{ textTransform: "none" }}
                    variant="contained"
                  >
                    Supprimer
                  </Button>
                </Box>}
              </Box>

              <Grid container spacing={2} sx={{ px: 2, py: 2 }}>
                {[
                  ["Code", activeDirection.direction_code],
                  ["Nom complet", activeDirection.direction_name],
                  ["Responsable", activeDirection.responsable_name],
                  ["Note", activeDirection.note],
                ].map(([label, value]) => (
                  <Grid item key={label} md={3} sm={6} xs={12}>
                    <Typography
                      color="#60758a"
                      fontSize={10}
                      fontWeight={800}
                      textTransform="uppercase"
                    >
                      {label}
                    </Typography>
                    <Typography color="#10263d" fontSize={13} mt={0.4}>
                      {String(value || "").trim() || "—"}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ borderTop: "1px solid #d5e0eb", px: 2, py: 1.5 }}>
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    justifyContent: "space-between",
                    mb: 1.25,
                  }}
                >
                  <Typography color="#173d61" fontSize={13} fontWeight={800}>
                    Processus rattachés
                  </Typography>
                  {isSmqAdmin && <Button
                    onClick={openCreateProcessDialog}
                    startIcon={<AddOutlinedIcon />}
                    sx={{
                      backgroundColor: "#173d61",
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#102f4c" },
                    }}
                    variant="contained"
                  >
                    Ajouter processus
                  </Button>}
                </Box>

                {processesError && (
                  <Alert
                    action={
                      <Button
                        color="inherit"
                        onClick={() => loadDirectionProcesses(activeDirectionId)}
                        size="small"
                      >
                        Réessayer
                      </Button>
                    }
                    severity="error"
                    sx={{ mb: 1.25 }}
                  >
                    {processesError}
                  </Alert>
                )}

                <Box sx={{ height: 420, minWidth: 0 }}>
                  <DataGrid
                    columns={processColumns}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.processus_id}
                    loading={processesLoading}
                    localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    rowHeight={58}
                    rows={directionProcesses}
                    sx={{
                      border: "1px solid #d5e0eb",
                      borderRadius: "7px",
                      color: "#10263d",
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 12,
                      "& .MuiDataGrid-cell": { borderColor: "#d5e0eb" },
                      "& .MuiDataGrid-columnHeader": {
                        backgroundColor: "#173d61",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                      },
                      "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                        outline: "2px solid #d5ad3f",
                        outlineOffset: "-2px",
                      },
                      "& .MuiDataGrid-columnSeparator, & .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": {
                        color: "#fff",
                      },
                      "& .MuiDataGrid-row:hover": { backgroundColor: "#f6f9fc" },
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          )}
        </Box>

        <DirectionAdd
          initialValues={dialog.initialValues}
          mode={dialog.mode}
          onClose={closeDialog}
          onSaved={handleSaved}
          open={dialog.open}
        />

        <ProcessusAdd
          initialValues={processDialog.initialValues}
          mode={processDialog.mode}
          onClose={closeProcessDialog}
          onSaved={handleProcessSaved}
          open={processDialog.open}
        />

        <Dialog
          fullWidth
          maxWidth="xs"
          onClose={closeDeleteDialog}
          open={Boolean(deleteTarget)}
          PaperProps={{ sx: { borderRadius: "12px" } }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid #cad7e5",
              color: "#10263d",
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            Supprimer la direction
          </DialogTitle>
          <DialogContent sx={{ pt: "20px !important" }}>
            {deleteError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {deleteError}
              </Alert>
            )}
            <DialogContentText color="#52677c" fontSize={13}>
              Confirmez-vous la suppression de «{" "}
              <Box component="strong" sx={{ color: "#10263d" }}>
                {deleteTarget?.direction_code} — {deleteTarget?.direction_name}
              </Box>{" "}
              » ?
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{ borderTop: "1px solid #cad7e5", gap: 1, px: 3, py: 1.5 }}
          >
            <Button
              color="inherit"
              disabled={deleting}
              onClick={closeDeleteDialog}
              sx={{ color: "#10263d", fontWeight: 700, textTransform: "none" }}
            >
              Annuler
            </Button>
            <Button
              color="error"
              disabled={deleting}
              onClick={handleDelete}
              sx={{ fontWeight: 700, minWidth: 110, textTransform: "none" }}
              variant="contained"
            >
              {deleting ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                "Supprimer"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          autoHideDuration={4000}
          onClose={() =>
            setNotification((current) => ({ ...current, open: false }))
          }
          open={notification.open}
        >
          <Alert
            elevation={6}
            onClose={() =>
              setNotification((current) => ({ ...current, open: false }))
            }
            severity={notification.severity}
            variant="filled"
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </>
    </SMQMainContainer>
  );
};

export default Direction;
